import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { destroyForm } from 'jl-design-system/redux/actions/form/formActions';

// Types
import { AppDispatch } from 'types/RootState.types';
import { PaymentCardProps } from 'types/Payment.types';
import { PayNowCategoryProps } from './PayNowCategory.types';

// Config
import payNowCategoryState from './PayNowCategory.state';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import {
  setSelectedSavedCard,
  setSelectedPaymentType,
  submitPayment,
  triggerPlaceOrderAndPayStartAnalytics,
} from '@redux/actions/payment/paymentActions';
import triggerAnalyticsEvent from '@redux/actions/analytics/analyticsAction';
import { saveTimePlaceOrderClicked } from '@redux/actions/timing/timingActions';
import { scrollToElement } from '@utils/index';
import { SAVED_CARD_DETAILS_FORM_ID } from '@utils/form/configs/savedCardDetails';
import {
  getPayNowPriorityOrder,
  priorityOrder as defaultPriorityOrder,
} from '@redux/actions/payment/selectedPaymentTypeActions';

// Components
import SavedCardOption from '../saved-card-option';
import ApplePayButton from '@components/add-payment-details/apple-pay-button/ApplePayButton';
import CardDetails from '@components/add-payment-details/card-details';
import { BillingDetails } from '@components/add-payment-details/card-details/billing-details';
import PayPalButton from '@components/add-payment-details/paypal-button';
import PaymentOption from '@components/add-payment-details/payment-option';
import GooglePayButton from '@components/google-pay-button/GooglePayButton';
import OrderTotals from '@components/order-totals';
import PartnerDiscountPaymentUnavailable from '@components/partner-discount-payment-unavailable';
import PaymentButton from '@components/payment-button';
import AddNewCard from '@components/streamlined-payments/pay-now-category/AddNewCard';
import TermsConditions from '@components/terms-conditions';

// Styles
import styles from './pay-now-category.scss';

const PayNowCategory = ({
  addNewCardProps = {},
  desktopResolution,
  disabled = false,
  isApplePayAvailable,
  isGooglePayAvailable,
  isPayPalAvailable,
  partnerDiscountEnabled,
  payPalPayLaterEligible,
  resetAfterError,
  showOrderTotals = false,
}: PayNowCategoryProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    confirmedDeliveryAddress,
    isCardFormValid,
    requestBillingAddressFromApplePay,
    savedCardFormSubmitFailed,
    savedPaymentCards = [],
    selectedBillingAddress,
    selectedDeliveryChoiceId,
    selectedPaymentType,
    selectedSavedCard,
    stripeCheckoutFlowEnabled,
  } = useSelector(payNowCategoryState);

  const priorityOrder = useMemo(() => dispatch(getPayNowPriorityOrder()) || defaultPriorityOrder, []);

  const togglePaymentOption = (paymentType = '') => {
    const clickedPaymentOptionIsAlreadySelected = selectedPaymentType === paymentType;

    if (!clickedPaymentOptionIsAlreadySelected) {
      // Ensure saved card details forms are destroyed before selecting a new payment type
      dispatch(destroyForm(SAVED_CARD_DETAILS_FORM_ID));
      setTimeout(() => {
        dispatch(setSelectedPaymentType({ paymentType, savedPaymentCards }));

        scrollToElement({
          id: paymentType,
        });
      }, 0);
    }
  };

  const renderPaymentOptionContent = (paymentType = '') => {
    const PayPalOptionContent = partnerDiscountEnabled ? <PartnerDiscountPaymentUnavailable /> : <PayPalButton />;

    const paymentOptionMapping: Record<string, JSX.Element> = {
      [paymentTypeConstants.GOOGLE_PAY]: <GooglePayButton />,
      [paymentTypeConstants.APPLE_PAY]: renderApplePay(),
      [paymentTypeConstants.PAYPAL]: PayPalOptionContent,
      [paymentTypeConstants.CREDIT_CARD]: <PaymentButton disabled={disabled} />,
    };

    const isCreditCard = paymentType === paymentTypeConstants.CREDIT_CARD;
    const paypalSelectedPartnerDiscountEnabled = paymentType === paymentTypeConstants.PAYPAL && partnerDiscountEnabled;

    if (paymentOptionMapping[paymentType]) {
      return (
        <div className={styles.paymentButtonContainer}>
          {showOrderTotals &&
            paymentType !== paymentTypeConstants.APPLE_PAY &&
            !paypalSelectedPartnerDiscountEnabled && <OrderTotals containerWide />}
          {paymentOptionMapping[paymentType]}
          {!isCreditCard && !paypalSelectedPartnerDiscountEnabled && <TermsConditions />}
        </div>
      );
    }

    return null;
  };

  const handleApplePayClick = () => {
    dispatch(saveTimePlaceOrderClicked());
    dispatch(triggerPlaceOrderAndPayStartAnalytics('applepay'));
    dispatch(
      setSelectedPaymentType({
        paymentType: paymentTypeConstants.APPLE_PAY,
        savedPaymentCards,
        shouldSetBillingAddress: false,
      }),
    );
    dispatch(submitPayment());
  };

  const renderApplePay = () => {
    const renderOrderTotals = () => {
      if (!showOrderTotals) return null;

      return <OrderTotals containerWide />;
    };

    const renderApplePayButton = () => <ApplePayButton isSubmitting={disabled} onClick={handleApplePayClick} />;

    if (requestBillingAddressFromApplePay) {
      return (
        <>
          {renderOrderTotals()}
          {renderApplePayButton()}
        </>
      );
    }

    return (
      <>
        <BillingDetails disabled={disabled} />
        <div className={styles.applePayButtonContainer}>
          {renderOrderTotals()}
          {renderApplePayButton()}
        </div>
      </>
    );
  };

  const renderSavedCards = () =>
    savedPaymentCards.map((card: PaymentCardProps) => (
      <SavedCardOption
        key={card.id}
        card={card}
        confirmedDeliveryAddress={confirmedDeliveryAddress}
        data-testid={`payment-option-${paymentTypeConstants.SAVED_PAYMENT_CARD}`}
        desktopResolution={desktopResolution}
        destroyForm={(args) => dispatch(destroyForm(args))}
        disabled={disabled}
        formSubmitFailed={savedCardFormSubmitFailed}
        isCardFormValid={isCardFormValid}
        resetAfterError={resetAfterError}
        savedPaymentCards={savedPaymentCards}
        selectedBillingAddress={selectedBillingAddress}
        selectedDeliveryChoiceId={selectedDeliveryChoiceId}
        selectedPaymentType={selectedPaymentType}
        selectedSavedCard={selectedSavedCard}
        setSelectedPaymentType={(savedCardId, savedPaymentCards) => {
          dispatch(setSelectedPaymentType({ paymentType: savedCardId, savedPaymentCards }));
        }}
        setSelectedSavedCard={(id) => dispatch(setSelectedSavedCard(id))}
        showOrderTotals={showOrderTotals}
        submitPayment={() => dispatch(submitPayment())}
        triggerAnalyticsEvent={(type, subType) => dispatch(triggerAnalyticsEvent(type, subType))}
      />
    ));

  const { APPLE_PAY, GOOGLE_PAY, SAVED_PAYMENT_CARD, PAYPAL, CREDIT_CARD } = paymentTypeConstants;

  return (
    <>
      {disabled && <div className={styles.payNowOverlay} data-testid="disable-payments" />}
      {priorityOrder.map((method: string) => {
        if (method === CREDIT_CARD) {
          return <AddNewCard key={CREDIT_CARD} {...addNewCardProps} />;
        }

        if (method === APPLE_PAY && isApplePayAvailable) {
          return (
            <PaymentOption
              key={APPLE_PAY}
              data-testid={`payment-option-${APPLE_PAY}`}
              desktopResolution={desktopResolution}
              disabled={disabled}
              icons={[styles.applePayIcon]}
              id={APPLE_PAY}
              label="Apple Pay"
              name="paymentOption"
              onClick={togglePaymentOption}
              selected={selectedPaymentType === APPLE_PAY}
            >
              {renderPaymentOptionContent(APPLE_PAY)}
            </PaymentOption>
          );
        }

        if (method === GOOGLE_PAY && isGooglePayAvailable) {
          return (
            <PaymentOption
              key={GOOGLE_PAY}
              data-testid={`payment-option-${GOOGLE_PAY}`}
              desktopResolution={desktopResolution}
              disabled={disabled}
              icons={[styles.googlePayIcon]}
              id={GOOGLE_PAY}
              label="Google Pay"
              name="paymentOption"
              onClick={togglePaymentOption}
              selected={selectedPaymentType === GOOGLE_PAY}
            >
              {renderPaymentOptionContent(GOOGLE_PAY)}
            </PaymentOption>
          );
        }

        if (method === SAVED_PAYMENT_CARD && !!savedPaymentCards.length) {
          return renderSavedCards();
        }

        if (method === PAYPAL && isPayPalAvailable) {
          return (
            <PaymentOption
              key={PAYPAL}
              data-testid={`payment-option-${PAYPAL}`}
              desktopResolution={desktopResolution}
              disabled={disabled}
              icons={[styles.paypalIcon]}
              id={PAYPAL}
              isPaypalPayLaterEligible={payPalPayLaterEligible?.isEligible && payPalPayLaterEligible?.isEnabled}
              label="PayPal"
              name="paymentOption"
              onClick={togglePaymentOption}
              selected={selectedPaymentType === PAYPAL}
            >
              {renderPaymentOptionContent(PAYPAL)}
            </PaymentOption>
          );
        }

        return null;
      })}

      {!stripeCheckoutFlowEnabled && (
        <PaymentOption
          data-testid={`payment-option-${paymentTypeConstants.PREPAID_CARD}`}
          desktopResolution={desktopResolution}
          disabled={disabled}
          icons={[styles.prepaidCardIcon]}
          id={paymentTypeConstants.PREPAID_CARD}
          label="Prepaid card"
          name="paymentOption"
          onClick={togglePaymentOption}
          selected={selectedPaymentType === paymentTypeConstants.PREPAID_CARD}
        >
          <div className={styles.newCardButtonContainer}>
            <div className={styles.newCardDetails}>
              <CardDetails disabled={disabled} prepaidCard resetAfterError={resetAfterError} />
            </div>
            {renderPaymentOptionContent(paymentTypeConstants.CREDIT_CARD)}
          </div>
        </PaymentOption>
      )}
    </>
  );
};

export default PayNowCategory;
